import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import {
  copyChart,
  createChart,
  deleteChart,
  updateChart,
} from '../../chartService'
import {
  draftChartFromText,
  explainChart,
  suggestCharts,
} from '../../chartAiService'
import type { ChartSeries, ChartSpec } from '../../../types/chart'
import type { WorkspaceAction } from '../../../utils/workspacePermissions'

export const CHART_CREATE_ACTION_ID = 'chart.create'
export const CHART_UPDATE_ACTION_ID = 'chart.update'
export const CHART_COPY_ACTION_ID = 'chart.copy'
export const CHART_DELETE_ACTION_ID = 'chart.delete'
export const CHART_DRAFT_FROM_TEXT_ACTION_ID = 'chart.draftFromText'
export const CHART_EXPLAIN_ACTION_ID = 'chart.explain'
export const CHART_SUGGEST_ACTION_ID = 'chart.suggest'

export type ChartActionArgs = {
  workspaceId?: string
  chartId?: string
  title?: string
  fromKit?: boolean
  confirm?: boolean
  text?: string
  mode?: string
}

export const chartActionArgsSchema: JSONSchemaType<ChartActionArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
    chartId: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
    fromKit: { type: 'boolean', nullable: true },
    confirm: { type: 'boolean', nullable: true },
    text: { type: 'string', nullable: true },
    mode: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

function extractSpec(args: unknown): ChartSpec | undefined {
  if (typeof args === 'object' && args !== null && 'spec' in args) {
    const raw = (args as { spec?: unknown }).spec
    if (typeof raw === 'object' && raw !== null) {
      return raw as ChartSpec
    }
  }
  return undefined
}

function extractSeries(args: unknown): ChartSeries[] {
  if (typeof args === 'object' && args !== null && 'series' in args) {
    const raw = (args as { series?: unknown }).series
    if (Array.isArray(raw)) {
      return raw as ChartSeries[]
    }
  }
  return []
}

const makeChartAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  requiredPermission: WorkspaceAction = 'chart.manage',
): ActionDefinition<ChartActionArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'chart',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chartActionArgsSchema,
  permission: (context) => context.canPerform(requiredPermission),
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'chart-correlation'
    const workspaceId = args.workspaceId ?? context.workspaceId

    if (!workspaceId) {
      const message = `workspaceId is required for ${label}`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }

    try {
      if (id === CHART_CREATE_ACTION_ID) {
        const spec = extractSpec(args)
        if (!spec) {
          throw new Error('spec is required for chart.create')
        }
        const res = await createChart(workspaceId, spec, args.fromKit)
        return {
          kind: 'COMPLETED',
          message: `Chart created: ${res.chartId}`,
          result: res,
          correlationId,
        }
      }

      if (id === CHART_UPDATE_ACTION_ID) {
        if (!args.chartId) {
          throw new Error('chartId is required for chart.update')
        }
        const spec = extractSpec(args)
        if (!spec) {
          throw new Error('spec is required for chart.update')
        }
        const res = await updateChart(workspaceId, args.chartId, spec)
        return {
          kind: 'COMPLETED',
          message: `Chart updated: ${res.chartId}`,
          result: res,
          correlationId,
        }
      }

      if (id === CHART_COPY_ACTION_ID) {
        if (!args.chartId) {
          throw new Error('chartId is required for chart.copy')
        }
        const res = await copyChart(workspaceId, args.chartId, args.title)
        return {
          kind: 'COMPLETED',
          message: `Chart copied: ${res.chartId}`,
          result: res,
          correlationId,
        }
      }

      if (id === CHART_DELETE_ACTION_ID) {
        if (!args.chartId) {
          throw new Error('chartId is required for chart.delete')
        }
        const res = await deleteChart(workspaceId, args.chartId, args.confirm)
        if (res.needsConfirm) {
          return {
            kind: 'NEEDS_CONFIRMATION',
            message: `Chart is placed in ${res.placements} location(s). Confirm delete?`,
            confirmPayload: res,
            correlationId,
          }
        }
        return {
          kind: 'COMPLETED',
          message: 'Chart deleted successfully',
          result: res,
          correlationId,
        }
      }

      if (id === CHART_DRAFT_FROM_TEXT_ACTION_ID) {
        if (!args.text) {
          throw new Error('text is required for chart.draftFromText')
        }
        const res = await draftChartFromText(workspaceId, args.text)
        return {
          kind: 'COMPLETED',
          message: res.ok ? 'Chart draft generated' : `Chart draft failed: ${res.reason}`,
          result: res,
          correlationId,
        }
      }

      if (id === CHART_EXPLAIN_ACTION_ID) {
        const spec = extractSpec(args)
        if (!spec) {
          throw new Error('spec is required for chart.explain')
        }
        const series = extractSeries(args)
        const res = await explainChart(workspaceId, spec, series)
        return {
          kind: 'COMPLETED',
          message: res.ok ? 'Chart explanation generated' : 'Chart explanation failed',
          result: res,
          correlationId,
        }
      }

      if (id === CHART_SUGGEST_ACTION_ID) {
        const mode = args.mode === 'auto' ? 'auto' : 'request'
        const res = await suggestCharts(workspaceId, mode)
        return {
          kind: 'COMPLETED',
          message: res.ok ? `Generated ${res.specs.length} chart suggestions` : `Chart suggestions failed: ${res.reason}`,
          result: res,
          correlationId,
        }
      }

      return {
        kind: 'FAILED',
        message: `Unknown chart action: ${id}`,
        recoverable: false,
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
})

export const chartCreateAction = makeChartAction(
  CHART_CREATE_ACTION_ID,
  'Create Chart',
  'Create a new custom chart.',
  'LOW',
)

export const chartUpdateAction = makeChartAction(
  CHART_UPDATE_ACTION_ID,
  'Update Chart',
  'Update an existing custom chart.',
  'LOW',
)

export const chartCopyAction = makeChartAction(
  CHART_COPY_ACTION_ID,
  'Copy Chart',
  'Duplicate a custom chart.',
  'LOW',
)

export const chartDeleteAction = makeChartAction(
  CHART_DELETE_ACTION_ID,
  'Delete Chart',
  'Delete a custom chart.',
  'HIGH',
)

export const chartDraftFromTextAction = makeChartAction(
  CHART_DRAFT_FROM_TEXT_ACTION_ID,
  'Draft Chart From Text',
  'Draft a custom chart using AI.',
  'LOW',
  'ai.use',
)

export const chartExplainAction = makeChartAction(
  CHART_EXPLAIN_ACTION_ID,
  'Explain Chart',
  'Explain a chart series using AI.',
  'READ_ONLY',
  'ai.use',
)

export const chartSuggestAction = makeChartAction(
  CHART_SUGGEST_ACTION_ID,
  'Suggest Charts',
  'Suggest charts for workspace data using AI.',
  'LOW',
  'ai.use',
)

export function registerChartActions(): void {
  if (!getAction(CHART_CREATE_ACTION_ID)) registerAction(chartCreateAction)
  if (!getAction(CHART_UPDATE_ACTION_ID)) registerAction(chartUpdateAction)
  if (!getAction(CHART_COPY_ACTION_ID)) registerAction(chartCopyAction)
  if (!getAction(CHART_DELETE_ACTION_ID)) registerAction(chartDeleteAction)
  if (!getAction(CHART_DRAFT_FROM_TEXT_ACTION_ID)) registerAction(chartDraftFromTextAction)
  if (!getAction(CHART_EXPLAIN_ACTION_ID)) registerAction(chartExplainAction)
  if (!getAction(CHART_SUGGEST_ACTION_ID)) registerAction(chartSuggestAction)
}

registerChartActions()
